<!-- src/pages/app/feedback/preference/index.vue -->
<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import PreferenceDrawer from '@/views/feedback/preference/PreferenceDrawer.vue'

definePage({
  meta: {
    subject: 'feedback.menu',
    action: 'preference',
  },
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

const searchQuery = ref('')
const debouncedSearch = ref('')
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
  }, 500)
})

// Filter tabs
const activeFilter = ref('all')

const filterTabs = [
  { title: 'All Admitted', value: 'all' },
  { title: 'With Preferences', value: 'with_preferences' },
  { title: 'Without Preferences', value: 'without_preferences' },
]

// Pagination refs
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

const hasPreferences = computed(() => {
  if (activeFilter.value === 'with_preferences') return true
  if (activeFilter.value === 'without_preferences') return false

  return undefined
})

const { data: patientsData, isFetching, execute: fetchPatients } = await useApi(
  createUrl('/masters/episodes', {
    query: {
      paged: 1,
      q: debouncedSearch,
      itemsPerPage,
      page,
      sort: sortBy,
      order_by: orderBy,
      is_ipd_form_type: true,
      has_preferences: hasPreferences,
      search: {
        unit_id: unitId.value,
        discharge_date: 'null',
      },
    },
  }),
)

const patients = computed(() => patientsData.value?.data ?? patientsData.value ?? [])
const totalPatients = computed(() => patientsData.value?.meta?.total ?? patientsData.value?.total ?? 0)

const headers = [
  {
    title: t('Customer'),
    key: 'customer',
    sortable: false,
  },
  {
    title: 'Phone',
    key: 'phone',
    sortable: false,
  },
  {
    title: 'Location',
    key: 'location',
    sortable: false,
  },
  {
    title: t('Admitted'),
    key: 'admitted',
  },
  {
    title: 'Preferences',
    key: 'preferences',
    sortable: false,
  },
  {
    title: '',
    key: 'actions',
    sortable: false,
    width: 40,
  },
]

const getDayCount = visitDateTime => {
  if (!visitDateTime) return ''

  const visitDate = new Date(visitDateTime)
  const today = new Date()
  const diffTime = Math.abs(today - visitDate)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  return diffDays === 1 ? '1 Day' : `${diffDays} Days`
}

const onRowClick = patient => {
  router.push({
    name: 'app-feedback-preference-id',
    params: { id: patient.customer?.id ?? patient.customer_id },
  })
}

// Preference Drawer
const isPreferenceDrawerOpen = ref(false)
const drawerPatient = ref(null)

const openDrawer = patient => {
  drawerPatient.value = patient
  isPreferenceDrawerOpen.value = true
}

// Handle ?customer= query param (e.g., from notification deep link)
const customerQueryParam = route.query.customer

if (customerQueryParam) {
  drawerPatient.value = { customer: { id: Number(customerQueryParam) }, id: null }
  isPreferenceDrawerOpen.value = true
}

const isExporting = ref(false)

const exportPreferences = async () => {
  isExporting.value = true

  const url = createUrl('feedbacks/preferences/export', {
    query: {
      unit_id: unitId.value,
      has_preferences: hasPreferences.value,
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(() => {})

  isExporting.value = false
}
</script>

<template>
  <div>
    <!-- Header -->
    <!--
      <div class="d-flex align-center gap-4 mb-6">
      <div>
      <h4 class="text-h4">
      Preferences
      </h4>
      <p class="text-body-2 text-disabled mb-0">
      Currently showing {{ totalPatients }} admitted {{ t('customer') }}s
      </p>
      </div>
      </div> 
    -->

    <!-- Card with Search + Table -->
    <VCard
      variant="flat"
      class="border"
    >
      <!-- Filter Tabs -->
      <VTabs
        v-model="activeFilter"
        class="mt-1"
        bg-color="transparent"
      >
        <VTab
          v-for="tab in filterTabs"
          :key="tab.value"
          :value="tab.value"
        >
          {{ tab.title }}
        </VTab>
      </VTabs>

      <!-- Search and Refresh -->
      <VCardText class="d-flex align-center gap-4 py-4">
        <AppTextField
          v-model="searchQuery"
          placeholder="Search by patient name, UHID, bed number..."
          density="compact"
          style="max-inline-size: 400px;"
        />
        <VSpacer />

        <VBtn
          v-if="$can('export', 'feedback.preference')"
          variant="tonal"
          color="primary"
          prepend-icon="tabler-download"
          :loading="isExporting"
          @click="exportPreferences"
        >
          Export
        </VBtn>
        <IconBtn
          variant="tonal"
          class="rounded"
          :loading="isFetching"
          @click="fetchPatients"
        >
          <VIcon
            size="24"
            icon="tabler-refresh"
          />
        </IconBtn>
      </VCardText>

      <VDivider />

      <!-- Table -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="patients"
        :items-length="totalPatients"
        :headers="headers"
        :loading="isFetching"
        fixed-header
        class="text-no-wrap"
        @update:options="updateOptions"
        @click:row="(_, { item }) => openDrawer(item)"
      >
        <!-- Patient Column -->
        <template #item.customer="{ item }">
          <div class="d-flex align-center gap-3 py-2">
            <VAvatar
              :color="item.customer?.color ?? 'primary'"
              variant="tonal"
              size="38"
            >
              <span class="text-sm font-weight-medium">
                {{ avatarText(item.customer?.name ?? '') }}
              </span>
            </VAvatar>
            <div>
              <div class="d-flex align-center gap-1">
                <span class="text-body-1 font-weight-medium">
                  {{ item.customer?.name }}
                </span>
              </div>
              <span class="text-body-2 text-medium-emphasis">
                {{ item.customer?.code }}
                <template v-if="item.customer?.age">
                  · {{ item.customer?.age }}y
                </template>
                <template v-if="item.customer?.gender">
                  · {{ item.customer?.gender?.charAt(0)?.toUpperCase() }}
                </template>
              </span>
            </div>
          </div>
        </template>

        <!-- Phone Column -->
        <template #item.phone="{ item }">
          <span class="text-body-2">
            {{ item.customer?.phone ?? '—' }}
          </span>
        </template>

        <!-- Location Column -->
        <template #item.location="{ item }">
          <div>
            <span class="text-body-2 font-weight-medium">
              {{ item.location?.name ?? '—' }}
            </span>
          </div>
        </template>

        <!-- Admitted Column -->
        <template #item.admitted="{ item }">
          <div>
            <div class="text-body-2">
              {{ formatDate(item.visit_date_time) }}
            </div>
            <span class="text-body-2 text-medium-emphasis">
              {{ getDayCount(item.visit_date_time) }}
            </span>
          </div>
        </template>

        <!-- Preferences Column -->
        <template #item.preferences="{ item }">
          <span
            v-if="item.customer?.preferences_count"
            class="text-body-2 font-weight-medium"
          >
            {{ item.customer.preferences_count }} Records
          </span>
          <span
            v-else
            class="text-body-2 text-medium-emphasis"
          >
            —
          </span>
        </template>

        <!-- Actions Column -->
        <template #item.actions="{ item }">
          <IconBtn
            size="small"
            variant="text"
          >
            <VIcon
              icon="tabler-chevron-right"
              size="20"
            />
            <VTooltip
              activator="parent"
              location="top"
            >
              View Preferences
            </VTooltip>
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- Preference Drawer -->
    <PreferenceDrawer
      v-if="drawerPatient?.customer?.id"
      v-model:is-drawer-open="isPreferenceDrawerOpen"
      :customer="drawerPatient.customer"
      :customer-id="drawerPatient.customer?.id"
      :episode-id="drawerPatient.id"
    />
  </div>
</template>

<style lang="scss" scoped>
:deep(.v-data-table) {
  tr {
    cursor: pointer;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }
  }
}
</style>

<style lang="css">
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
